import Vue from 'vue'
import UiScrollbar from './ui-scrollbar.vue'
import { DirectiveBinding } from 'vue/types/options'

type TTargetElement = Element | undefined

type TRuleType = string | number | boolean

type TRuleProps = Record<string, TRuleType>

type TRuleValidator = Record<string, (value: TRuleType) => boolean>

type TDirectiveHelper = {
  scrollbar: InstanceType<typeof UiScrollbar> & {
    targetElement: TTargetElement
  }
  targetElement: TTargetElement
  mountScrollbar: () => void
  changeOrientation: () => void
  setProperties: (props: TRuleProps) => void
  getTargetElement: (el: Element, props: TRuleProps) => Element
}

/**
 * ? isString
 * Признак строки
 *
 * @param {string} value строковое значение
 * @returns {boolean} признак валидности строки
 */
const isString = (value: string): boolean => {
  return typeof value === 'string'
}

/**
 * ? isNumber
 * Признак числа
 *
 * @param {string} value числовое значение
 * @returns {boolean} признак валидности числа
 */
const isNumber = (value: string): boolean => {
  return typeof value === 'number'
}

/**
 * ? isBoolean
 * Признак логического значения
 *
 * @param {boolean} value логическое значение
 * @returns {boolean} признак валидности логического значения
 */
const isBoolean = (value: string): boolean => {
  return typeof value === 'boolean'
}

/**
 * ? isAppearMode
 * Признак валидности значений в режиме появления ползунка
 *
 * @param {string} value значения в режиме появления ползунка
 * @returns {boolean} признак валидности значений в режиме появления ползунка
 */
const isAppearMode = (value: string): boolean => {
  return ['', 'hover', 'touch'].includes(value)
}

/**
 * ? isSliderSize
 * Признак валидности размера полосы прокрутки
 *
 * @param {string} value значение размера полосы прокрутки
 * @returns {boolean} признак валидности размера полосы прокрутки
 */
const isSliderSize = (value: string): boolean => {
  return ['sm'].includes(value)
}

/**
 * ? checkRule
 * Проверить правило
 *
 * @param {string} key ключ свойства
 * @param {TRuleType} value значение свойства
 * @returns {boolean} признак проверки правила
 */
const checkRule = (key: string, value: TRuleType): boolean => {
  const rule = {
    width: isNumber,
    height: isNumber,
    sliderSize: isSliderSize,
    appearMode: isAppearMode,
    hideScrollbarX: isBoolean,
    hideScrollbarY: isBoolean,
    disableScrollbar: isBoolean,
    nestedTargetClass: isString,
  }

  if (!(key in rule)) {
    return true
  }

  return (rule as TRuleValidator)[key](value)
}

/**
 * ? isValidRules
 * Признак валидности всех правил
 *
 * @param {TRuleProps} props реквизиты компонента
 * @returns {boolean} признак валидности всех правил
 */
const isValidRules = (props: TRuleProps): boolean => {
  return Object.entries(props).every(([key, value]) => {
    return (
      checkRule(key, value) ||
      console.error(
        `[Directive v-scrollbar]: Type error for property "${key}".`
      )
    )
  })
}

const $h: TDirectiveHelper = {
  targetElement: undefined,
  scrollbar: new Vue(UiScrollbar),

  /**
   * ? mountScrollbar
   * Монтировать scrollbar на найденный элемент узла
   *
   * @returns {void}
   */
  mountScrollbar(): void {
    this.scrollbar.$mount(this.targetElement)
  },

  /**
   * ? changeOrientation
   * Изменить ориентацию и обнулить значение высоты скролла элемента
   *
   * @returns {void}
   */
  changeOrientation(): void {
    Vue.set($h.scrollbar, 'targetElementScrollHeight', 0)
  },

  /**
   * ? getTargetElement
   * Получить целевой элемент привязки
   *
   * @param {Element} el первичный привязанный элемент (куда повесили v-scrollbar)
   * @param {TRuleProps} props реквизиты компонента ui-scrollbar
   * @returns {Element} целевой элемент привязки
   */
  getTargetElement(el: Element, props: TRuleProps = {}): Element {
    const { nestedTargetClass } = props

    return nestedTargetClass
      ? el.querySelector(`.${nestedTargetClass}`) ?? el
      : el
  },

  /**
   * ? setProperties
   * Установить / обновить реквизиты для компонента ui-scrollbar
   *
   * @param {TRuleProps} props реквизиты компонента ui-scrollbar
   * @returns {void}
   */
  setProperties(props: TRuleProps = {}): void {
    if (!isValidRules(props)) {
      return
    }

    // Сохранить целевой узел привязки
    if (!this.scrollbar.targetElement) {
      Vue.set(this.scrollbar, 'targetElement', this.targetElement)
    }

    // Сохранить / обновить высоту скролла элемента
    Vue.set(this.scrollbar, 'targetElementScrollHeight', 0)
    Vue.nextTick(() => {
      Vue.set(
        this.scrollbar,
        'targetElementScrollHeight',
        this.targetElement?.scrollHeight
      )
    })

    // Сохранить / обновить переданные реквизиты из вне
    for (const key in props) {
      Vue.set(this.scrollbar.$props, key, props[key])
    }
  },
}

// Имя директивы
export const name = 'scrollbar'

// Инициализация / обновление директивы
export const directive = {
  bind(el: Element, binding: DirectiveBinding) {
    $h.scrollbar = new Vue(UiScrollbar)
    $h.targetElement = $h.getTargetElement(el, binding.value)

    window.addEventListener('orientationchange', $h.changeOrientation)
  },

  inserted(el: Element, binding: DirectiveBinding) {
    $h.setProperties(binding.value)
    $h.mountScrollbar()
  },

  componentUpdated(el: Element, binding: DirectiveBinding) {
    $h.setProperties(binding.value)
  },

  unbind() {
    window.removeEventListener('orientationchange', $h.changeOrientation)
  },
}
